/**
 * List controls: saved views, active-filter pills, group-by, and the bulk bar.
 * These drive server-side state (a saved view is a URL, UX-002 §2.2); they hold
 * no data of their own.
 */
import type { ReactNode } from "react";

/* ── SavedViews ─────────────────────────────────────────────────────────────
   Named views the person saved (each is a URL). Selecting one applies it. */
export interface SavedView {
  id: string;
  name: string;
}

export function SavedViews(props: {
  views: SavedView[];
  activeId?: string;
  onSelect: (id: string) => void;
  onSave?: () => void;
}): ReactNode {
  return (
    <div className="sk-savedviews" role="group" aria-label="Saved views">
      {props.views.map((v) => (
        <button
          key={v.id}
          type="button"
          className="sk-savedview"
          aria-pressed={v.id === props.activeId}
          onClick={() => props.onSelect(v.id)}
        >
          {v.name}
        </button>
      ))}
      {props.onSave ? (
        <button type="button" className="sk-savedview sk-savedview--save" onClick={props.onSave}>
          + Save view
        </button>
      ) : null}
    </div>
  );
}

/* ── FilterPills ────────────────────────────────────────────────────────────
   The active filters, each removable; the set is reflected in the URL. */
export interface ActiveFilter {
  key: string;
  label: string;
  value: string;
}

export function FilterPills(props: {
  filters: ActiveFilter[];
  onRemove: (key: string) => void;
  onClear?: () => void;
}): ReactNode {
  if (props.filters.length === 0) return null;
  return (
    <div className="sk-filterpills" role="group" aria-label="Active filters">
      {props.filters.map((f) => (
        <span className="sk-filterpill" key={f.key}>
          <span className="sk-filterpill__label">{f.label}:</span>
          <span className="sk-filterpill__value">{f.value}</span>
          <button
            type="button"
            className="sk-filterpill__remove"
            aria-label={`Remove filter ${f.label}`}
            onClick={() => props.onRemove(f.key)}
          >
            ✕
          </button>
        </span>
      ))}
      {props.onClear ? (
        <button type="button" className="sk-filterpills__clear" onClick={props.onClear}>
          Clear all
        </button>
      ) : null}
    </div>
  );
}

/* ── GroupBy ────────────────────────────────────────────────────────────────
   Pick a grouping dimension (server groups the rows). */
export interface GroupDimension {
  key: string;
  label: string;
}

export function GroupBy(props: {
  dimensions: GroupDimension[];
  value?: string;
  onChange: (key: string | undefined) => void;
}): ReactNode {
  return (
    <label className="sk-groupby">
      <span className="sk-groupby__label">Group by</span>
      <select
        className="sk-groupby__select"
        value={props.value ?? ""}
        onChange={(e) => props.onChange(e.target.value || undefined)}
      >
        <option value="">None</option>
        {props.dimensions.map((d) => (
          <option key={d.key} value={d.key}>
            {d.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ── BulkBar ────────────────────────────────────────────────────────────────
   Acts on a visible selection, with a count and a preview of what will happen
   (UX-003 §7). Renders only when something is selected. */
export interface BulkAction {
  key: string;
  label: string;
  onRun: () => void;
}

export function BulkBar(props: {
  count: number;
  actions: BulkAction[];
  onClear: () => void;
}): ReactNode {
  if (props.count <= 0) return null;
  return (
    <div className="sk-bulkbar" role="toolbar" aria-label="Bulk actions">
      <span className="sk-bulkbar__count">{props.count} selected</span>
      <span className="sk-bulkbar__actions">
        {props.actions.map((a) => (
          <button key={a.key} type="button" className="sk-bulkbar__action" onClick={a.onRun}>
            {a.label}
          </button>
        ))}
      </span>
      <button type="button" className="sk-bulkbar__clear" onClick={props.onClear}>
        Clear
      </button>
    </div>
  );
}
